import {
    format,
    formatDuration,
    getMonth,
    getYear,
    intervalToDuration,
    isToday,
    isTomorrow,
    isYesterday,
    parse,
    setHours,
    setMinutes,
} from 'date-fns';
import ru from 'date-fns/locale/ru';

import { RANGE_DATE_PLACEHOLDER_TEXT } from '@utils/constants';
import { isValueSet } from '@utils/guards';
import { zeroPad } from '@utils/number';

const formatRuDate = (date: Date, dateFormat: string) => format(date, dateFormat, { locale: ru });

export const formatDate = (date: Date, canHideYear = true) => {
    let dateFormat = 'd MMMM yyyy';
    if (isYesterday(date)) return 'Вчера';
    if (isToday(date)) return 'Сегодня';
    if (isTomorrow(date)) return 'Завтра';
    if (canHideYear && getYear(date) === getYear(new Date())) dateFormat = 'd MMMM';
    return formatRuDate(date, dateFormat);
};

const makeRangeDateString = (
    startDate: Date,
    startDateFormat: string,
    endDate?: Date | null,
    endDateFormat?: string | null,
): string => {
    return `С ${formatRuDate(startDate, startDateFormat)}${
        isValueSet(endDate) && isValueSet(endDateFormat)
            ? ` по ${formatRuDate(endDate, endDateFormat)}`
            : ''
    }`;
};

export const formatRangeDate = (startDate?: Date | null, endDate?: Date | null) => {
    if (isValueSet(startDate) && isValueSet(endDate)) {
        const currentYear = getYear(new Date());
        if (getYear(startDate) === currentYear && getYear(endDate) === currentYear) {
            if (getMonth(startDate) === getMonth(endDate)) {
                return makeRangeDateString(startDate, 'd', endDate, 'd MMMM');
            }

            return makeRangeDateString(startDate, 'd MMMM', endDate, 'd MMMM');
        } else {
            return makeRangeDateString(startDate, 'd MMMM yyyy', endDate, 'd MMMM yyyy');
        }
    }

    if (isValueSet(startDate)) {
        const dateFormat = getYear(startDate) === getYear(new Date()) ? 'd MMMM' : 'd MMMM yyyy';

        return makeRangeDateString(startDate, dateFormat);
    }

    return RANGE_DATE_PLACEHOLDER_TEXT;
};

export const parseApiDate = (apiDate: FeipApi.Date) => parse(apiDate, 'yyyy.MM.dd', new Date());

export const formatApiDate = (apiDate: FeipApi.Date): string => {
    const date = parseApiDate(apiDate);
    return formatDate(date);
};

export const formatApiDateTime = (apiDate: FeipApi.DateTime, canHideYear = true): string =>
    formatDate(new Date(apiDate), canHideYear);

export const convertMinutestToSeconds = (minutes: number): number => minutes * 60;

export const formatApiDateTimeWithTime = (timestamp: FeipApi.DateTime): string =>
    formatRuDate(new Date(timestamp), 'dd.MM.yyyy, kk:mm');

export const formatApiDateTimeWithFullMonth = (apiDate: FeipApi.DateTime): string => {
    return formatRuDate(new Date(apiDate), 'dd MMMM yyyy');
};

export const getDateByTime = (date: Date, hours = 0, minutes = 0) =>
    setHours(setMinutes(date, minutes), hours);

export const formatDateForApi = (date: Date): string => {
    const dateFormat = 'yyyy.MM.dd';
    return format(date, dateFormat);
};

// HH:MM
export const formatHourAndMinutes = (time: number): string => {
    const duration = intervalToDuration({
        start: 0,
        end: time * 1000,
    });
    return formatDuration(duration, {
        format: ['hours', 'minutes'],
        zero: true,
        delimiter: ':',
        locale: {
            formatDistance: (_token, count) => zeroPad(count, 2),
        },
    });
};
